/**
 * Evidenziazione della sintassi per compose ed environment.
 *
 * Non è un parser YAML: è un tokenizzatore per riga, tarato sul sottoinsieme che
 * i file compose usano davvero. Un parser completo qui non servirebbe a nulla,
 * perché lo scopo è colorare del testo che l'utente sta ancora scrivendo — e
 * quindi spesso incompleto o momentaneamente invalido.
 *
 * Il vincolo da cui non si può derogare: i token, concatenati, devono
 * restituire la riga originale carattere per carattere. L'evidenziazione viene
 * disegnata sotto un'area di testo trasparente, e un solo carattere perso
 * disallineerebbe tutto il resto della riga.
 */

export type TokenType =
  | "comment"
  | "key"
  | "string"
  | "number"
  | "boolean"
  | "variable"
  | "punct"
  | "text";

export interface Token {
  readonly type: TokenType;
  readonly text: string;
}

/** Valori che YAML tratta come letterali e non come stringhe. */
const LITERALS = new Set([
  "true",
  "false",
  "null",
  "yes",
  "no",
  "on",
  "off",
  "~",
  // i18n-ignora: parole chiave YAML, non testo.
  "True", // i18n-ignora
  "False", // i18n-ignora
  "Null", // i18n-ignora
]);

/** `${VAR}`, `${VAR:-default}` e `$VAR`; il `$$` letterale è escluso. */
const VARIABLE = /\$\{[A-Za-z_][A-Za-z0-9_]*(?::?[-?][^}]*)?\}|\$[A-Za-z_][A-Za-z0-9_]*/g;

/** Chiave YAML: nome seguito da due punti e da uno spazio o dalla fine riga. */
const YAML_KEY = /^([A-Za-z_][\w.-]*)(:)(\s|$)/;

const push = (tokens: Token[], type: TokenType, text: string) => {
  if (text.length > 0) tokens.push({ type, text });
};

/**
 * Spezza un frammento isolando le variabili di interpolazione.
 *
 * Serve sia sui valori nudi sia dentro le stringhe quotate: in un compose la
 * porta si scrive `"${PORT_WEB}:80"`, e vedere la variabile evidenziata anche lì
 * è esattamente ciò che aiuta a non sbagliarla.
 */
function splitVariables(text: string, base: TokenType, tokens: Token[]): void {
  // Il dollaro raddoppiato è un dollaro letterale: neutralizzato con caratteri
  // della stessa lunghezza, così le posizioni restano valide sull'originale.
  const searchable = text.replaceAll("$$", "  ");

  let last = 0;
  for (const match of searchable.matchAll(VARIABLE)) {
    const start = match.index;
    push(tokens, base, text.slice(last, start));
    push(tokens, "variable", text.slice(start, start + match[0].length));
    last = start + match[0].length;
  }

  push(tokens, base, text.slice(last));
}

/** Posizione di un `#` che apre un commento, ignorando quelli dentro le stringhe. */
function commentStart(text: string): number {
  let quote: string | null = null;

  for (let i = 0; i < text.length; i++) {
    const char = text[i]!;

    if (quote !== null) {
      if (char === quote) quote = null;
      continue;
    }

    if (char === '"' || char === "'") {
      quote = char;
      continue;
    }

    // Un commento richiede uno spazio prima, altrimenti `a#b` sarebbe spezzato.
    if (char === "#" && (i === 0 || /\s/.test(text[i - 1]!))) return i;
  }

  return -1;
}

const isQuoted = (text: string) =>
  text.length >= 2 &&
  ((text.startsWith('"') && text.endsWith('"')) || (text.startsWith("'") && text.endsWith("'")));

/** Classifica un valore e lo aggiunge, isolandone le variabili. */
function pushValue(value: string, tokens: Token[]): void {
  const trimmed = value.trim();

  if (trimmed.length === 0) {
    push(tokens, "text", value);
    return;
  }

  // Spazi iniziali e finali restano testo neutro, così il valore resta allineato.
  const leading = value.slice(0, value.length - value.trimStart().length);
  const trailing = value.slice(value.trimEnd().length);

  push(tokens, "text", leading);

  if (isQuoted(trimmed)) {
    splitVariables(trimmed, "string", tokens);
  } else if (LITERALS.has(trimmed)) {
    push(tokens, "boolean", trimmed);
  } else if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
    push(tokens, "number", trimmed);
  } else {
    splitVariables(trimmed, "text", tokens);
  }

  push(tokens, "text", trailing);
}

export function highlightYamlLine(line: string): Token[] {
  const tokens: Token[] = [];
  if (line.length === 0) return tokens;

  // L'indentazione non è colorata ma va conservata: è ciò che allinea la riga.
  const indent = line.slice(0, line.length - line.trimStart().length);
  let rest = line.slice(indent.length);
  push(tokens, "text", indent);

  if (rest.length === 0) return tokens;

  // Commento che occupa l'intera riga.
  if (rest.startsWith("#")) {
    push(tokens, "comment", rest);
    return tokens;
  }

  // Commento in coda: isolato subito, così non interferisce col resto.
  const commentAt = commentStart(rest);
  const comment = commentAt === -1 ? "" : rest.slice(commentAt);
  if (commentAt !== -1) rest = rest.slice(0, commentAt);

  // Trattino di elenco: ciò che segue è a sua volta una riga, che può
  // contenere una chiave (`- target: 80`).
  if (/^-\s/.test(rest) || rest === "-") {
    const marker = rest === "-" ? "-" : rest.slice(0, 2);
    push(tokens, "punct", marker);
    rest = rest.slice(marker.length);
  }

  const key = YAML_KEY.exec(rest);
  if (key) {
    const [, name = "", colon = ""] = key;
    push(tokens, "key", name);

    // Lo spazio dopo i due punti viaggia con essi: un token in meno da allineare.
    const afterColon = rest.slice(name.length + colon.length);
    const space = afterColon.slice(0, afterColon.length - afterColon.trimStart().length);
    push(tokens, "punct", colon + space);

    pushValue(afterColon.slice(space.length), tokens);
  } else {
    pushValue(rest, tokens);
  }

  push(tokens, "comment", comment);

  return tokens;
}

/**
 * Nomi delle variabili di interpolazione presenti in un testo.
 *
 * Serve all'editor per segnalare, mentre si scrive, quali variabili il compose
 * richiede e quali sono di troppo. Non sostituisce la validazione del server —
 * che resta l'unica autorevole e usa un vero parser YAML — ma quella richiede
 * un giro di rete, e un riscontro immediato mentre si digita vale la piccola
 * sovrapposizione.
 */
export function variabiliUsate(testo: string): string[] {
  const trovate = new Set<string>();

  for (const match of testo.replaceAll("$$", "  ").matchAll(VARIABLE)) {
    // Scarta il simbolo, le eventuali graffe e il valore predefinito.
    const nome = match[0].replace(/^\$\{?/, "").replace(/[:?\-}].*$/, "");
    if (nome.length > 0) trovate.add(nome);
  }

  // `environment: - NOME` non interpola nulla ma consegna al container il
  // valore che ha nell'environment dello stack: è un uso a tutti gli effetti.
  for (const riga of testo.split("\n")) {
    const nome = EREDITATA.exec(riga)?.[1];
    if (nome) trovate.add(nome);
  }

  return [...trovate].sort();
}

/**
 * Voce di `environment:` senza valore: `- NOME`.
 *
 * Il maiuscolo distingue dagli altri elenchi del compose — `depends_on`,
 * `volumes`, `networks` — senza dover seguire l'annidamento del documento:
 * qui basta un'anticipazione, il verdetto è del parser sul server.
 */
const EREDITATA = /^\s*-\s*([A-Z_][A-Z0-9_]*)\s*$/;

/**
 * Il compose consegna a un servizio l'intero environment dello stack?
 *
 * `env_file: stack.env` fa arrivare dentro il container tutte le variabili,
 * quindi nessuna è di troppo. Come per il resto di questo file è
 * un'anticipazione testuale: il verdetto è del parser sul server.
 */
export function dichiaraStackEnv(testo: string): boolean {
  return /^\s*(-\s*)?env_file:\s*\S*stack\.env\s*$/m.test(testo) ||
    /^\s*-\s*\S*stack\.env\s*$/m.test(testo);
}

/**
 * Variabili che nel compose dichiarano un valore predefinito.
 *
 * `${LOG_LEVEL:-info}` non va segnalata come mancante: se nessuno la imposta,
 * Compose usa `info` e lo stack parte comunque. Contarla fra le variabili da
 * compilare manderebbe a cercare un problema che non esiste.
 */
export function variabiliConDefault(testo: string): string[] {
  const trovate = new Set<string>();
  // Solo le forme con `-`, che forniscono un ripiego; `:?` invece è un vincolo.
  const pattern = /\$\{([A-Za-z_][A-Za-z0-9_]*):?-[^}]*\}/g;

  for (const match of testo.replaceAll("$$", "  ").matchAll(pattern)) {
    if (match[1]) trovate.add(match[1]);
  }

  return [...trovate].sort();
}

/**
 * Variabile usata come porta host in una voce di `ports:`.
 * Riconosce `- "${PORT_WEB}:80"` e le sue varianti senza virgolette.
 */
const PORT_VARIABLE = /^\s*-\s*["']?\$\{?([A-Za-z_][A-Za-z0-9_]*)\}?\s*:\s*\d+/;

/**
 * Variabili che Portman assegna da sé, perché usate come porta host.
 *
 * L'editor le distingue da quelle che l'utente deve fornire: chiederle sarebbe
 * il contrario di ciò che l'assegnazione automatica promette. Anche qui la
 * parola definitiva resta al server, che analizza il compose con un vero parser.
 */
export function variabiliDiPorta(testo: string): string[] {
  const trovate = new Set<string>();

  for (const riga of testo.split("\n")) {
    const nome = PORT_VARIABLE.exec(riga)?.[1];
    if (nome) trovate.add(nome);
  }

  return [...trovate].sort();
}

/** `[export ]CHIAVE=` all'inizio della riga. */
const ENV_ASSIGNMENT = /^(export\s+)?([A-Za-z_][A-Za-z0-9_]*)(=)/;

export function highlightEnvLine(line: string): Token[] {
  const tokens: Token[] = [];
  if (line.length === 0) return tokens;

  const trimmedStart = line.trimStart();
  const indent = line.slice(0, line.length - trimmedStart.length);
  push(tokens, "text", indent);

  if (trimmedStart.length === 0) return tokens;

  if (trimmedStart.startsWith("#")) {
    push(tokens, "comment", trimmedStart);
    return tokens;
  }

  const assignment = ENV_ASSIGNMENT.exec(trimmedStart);
  if (!assignment) {
    push(tokens, "text", trimmedStart);
    return tokens;
  }

  const [matched, exportPrefix, name = "", equals = ""] = assignment;
  if (exportPrefix) push(tokens, "punct", exportPrefix);
  push(tokens, "key", name);
  push(tokens, "punct", equals);

  const value = trimmedStart.slice(matched.length);
  if (isQuoted(value.trim())) {
    splitVariables(value, "string", tokens);
  } else {
    splitVariables(value, "text", tokens);
  }

  return tokens;
}
